import { AccessTime, ExpandMore } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { memo, useEffect, useRef, useState } from 'react'
import { useMicroLearningHistory } from '../../hooks/useMicroLearningHistory'
import MicroLearningHistoryCard from './MicroLearningHistoryCard'
import styles from './MicroLearningHistoryCard.module.css'

type MicroLearningHistoryListProps = {
  mlId: string | undefined
  refreshSignal?: number
}

const MicroLearningHistoryList = ({ mlId, refreshSignal = 0 }: MicroLearningHistoryListProps) => {
  const { logs, isLoading, hasMore, loadMore } = useMicroLearningHistory(mlId, refreshSignal)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 1 },
    )

    observer.observe(sentinel)

    return () => {
      observer.unobserve(sentinel)
    }
  }, [loadMore])

  if (!mlId) return null

  const hasNoHistory = !isLoading && logs.length === 0 && !hasMore

  if (hasNoHistory) return null

  return (
    <div className={styles.historyContainer}>
      <button
        type="button"
        className={styles.historyHeader}
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse history' : 'Expand history'}>
        <div className={styles.historyHeaderLeft}>
          <AccessTime className={styles.historyClockIcon} />
          <span className={styles.historyTitle}>HISTORY</span>
        </div>
        <ExpandMore className={`${styles.historyChevron} ${expanded ? styles.historyChevronExpanded : ''}`} />
      </button>

      {expanded && (
        <div className={styles.historyBody}>
          <div className={styles.timelineWrapper}>
            {logs.length > 1 && <div className={styles.timelineLine} />}

            {logs.map((entry, idx) => (
              <MicroLearningHistoryCard
                key={`${entry.action}-${entry.created_at}-${idx}`}
                entry={entry}
                isFirst={idx === 0}
              />
            ))}

            {isLoading && (
              <div className={styles.loadingContainer}>
                <CircularProgress size={24} />
              </div>
            )}

            {hasMore && (
              <div
                ref={sentinelRef}
                className={styles.sentinel}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(MicroLearningHistoryList)
